import { useCallback, useEffect, useRef, useState } from 'react';
import Player from '@vimeo/player';
import { useListenerAudioPlayMutation, useListenerAudioPlaybackMutation } from '../../hooks/api/useListenerAudio';
import { Experience, Content, PlaybackLocation } from '../../hooks/api/useExperiences';
import { analytics, EventTypes } from '../../utils/analytics';
import { useEngagementEvent, useHubspotEventMutation } from '../../hooks/api/useEvent';
import useAuth from '../../hooks/utils/useAuth';
import { createMwaVimeoPlayerOptions } from '../../utils/vimeo-player-options';

export interface UseVimeoPlayerResult {
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Vimeo player hook (video setup with height and width support, robust init and cleanup).
 */
export function useVimeoPlayer(
  experience: Experience | Content,
  playbackLocation: PlaybackLocation,
  width?: number,
  height?: number,
): UseVimeoPlayerResult {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const vimeoPlayer = useRef<Player | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const playMutation = useListenerAudioPlayMutation();
  const playBackMutation = useListenerAudioPlaybackMutation();
  const hubspotMutation = useHubspotEventMutation();
  const postEngagementEvent = useEngagementEvent();
  const [hasBeenPlayed, setHasBeenPlayed] = useState<boolean>(false);
  const currentPlaybackIdRef = useRef<number>();
  const trackedPercents = useRef<Set<number>>(new Set());
  const videoId = experience.video_id!;
  const previousVideoIdRef = useRef<string | undefined>(videoId);

  const handlePlay = useCallback(
    async (data: { percent: number }) => {
      if (!vimeoPlayer.current) return;

      trackedPercents.current.clear();
      if (hasBeenPlayed) return;
      setHasBeenPlayed(true);

      if (data.percent === 0) {
        trackedPercents.current.clear(); // Reset tracked percentages when starting from beginning
        hubspotMutation.mutate({
          event: 'play_experience',
          properties: {
            experience_id: experience.id,
            media_type: 'video',
            playback_location: playbackLocation || 'search-results',
          },
        });
        analytics.trackEvent(EventTypes.PLAY_EXPERIENCE, experience.id, 'video', playbackLocation);

        let videoDuration = 0;
        try {
          const player = vimeoPlayer.current;
          if (player) {
            await player.ready();
            videoDuration = (await player.getDuration()) || 0;
          }
        } catch (error) {
          console.error('Error getting video duration:', error);
        }

        const playMutationResult = await playMutation.mutateAsync(
          {
            audioId: experience.id,
            listenerId: experience.listener_role_id,
          },
          {
            onSuccess: (listenerAudioPlayback) => {
              currentPlaybackIdRef.current = listenerAudioPlayback.id;
            },
          },
        );
        postEngagementEvent.mutate({
          engagementType: 'video_play',
          listenerAudioPlaybackId: playMutationResult.id,
          metaData: {
            user_id: user!.id,
            experience_id: experience.id,
            playback_location: playbackLocation || 'search-results',
            duration: videoDuration,
          },
        });
      }
    },
    [hasBeenPlayed, experience, playMutation, hubspotMutation, playbackLocation, user, postEngagementEvent],
  );

  const handleTimeUpdate = useCallback(
    async (data: { percent: number; seconds: number }) => {
      if (!currentPlaybackIdRef.current) {
        console.error('currentPlaybackId is not set for experience id:', experience.id);
        return;
      }
      const percentage = Math.floor(data.percent * 100);
      const lastTimeUpdate = Math.floor(percentage / 10) * 10;

      if (!trackedPercents.current.has(lastTimeUpdate) && lastTimeUpdate <= 100) {
        trackedPercents.current.add(lastTimeUpdate);
        playBackMutation.mutate({
          audioId: currentPlaybackIdRef.current,
          percentCompleted: lastTimeUpdate,
        });
      }
    },
    [experience.id, playBackMutation],
  );

  // Store callbacks in refs to avoid stale closures
  const handlePlayRef = useRef(handlePlay);
  const handleTimeUpdateRef = useRef(handleTimeUpdate);

  // Update callback refs when they change
  useEffect(() => {
    handlePlayRef.current = handlePlay;
    handleTimeUpdateRef.current = handleTimeUpdate;
  }, [handlePlay, handleTimeUpdate]);

  // Reset play state when experience changes
  useEffect(() => {
    if (previousVideoIdRef.current !== videoId) {
      setHasBeenPlayed(false);
      trackedPercents.current.clear();
      currentPlaybackIdRef.current = undefined;
      previousVideoIdRef.current = videoId;
    }
  }, [videoId]);

  useEffect(() => {
    if (!containerRef.current || isInitializingRef.current || !width || !height) return;

    const el = containerRef.current;
    let isMounted = true;
    let nextPlayer: Player | null = null;
    let readyPromise: Promise<void> | null = null;

    const destroyCurrentPlayer = () => {
      isInitializingRef.current = false;
      if (!vimeoPlayer.current) return;
      try {
        const oldPlayer = vimeoPlayer.current;
        if ((oldPlayer as any)._playHandler) {
          oldPlayer.off('play', (oldPlayer as any)._playHandler);
        }
        if ((oldPlayer as any)._timeUpdateHandler) {
          oldPlayer.off('timeupdate', (oldPlayer as any)._timeUpdateHandler);
        }
        oldPlayer.destroy();
      } catch {
        // Player may already be destroyed - ignore
      }
      vimeoPlayer.current = null;
    };

    destroyCurrentPlayer();
    isInitializingRef.current = true;

    try {
      nextPlayer = new Player(el, createMwaVimeoPlayerOptions(videoId, { width, height }));
      vimeoPlayer.current = nextPlayer;

      readyPromise = nextPlayer
        .ready()
        .then(() => {
          if (!isMounted || !vimeoPlayer.current || vimeoPlayer.current !== nextPlayer) {
            isInitializingRef.current = false;
            return;
          }

          try {
            const playHandler = (data: { percent: number }) => handlePlayRef.current(data);
            const timeUpdateHandler = (data: { percent: number; seconds: number }) => handleTimeUpdateRef.current(data);

            nextPlayer!.on('play', playHandler);
            nextPlayer!.on('timeupdate', timeUpdateHandler);
            nextPlayer!.on('error', (error) => {
              console.error('Vimeo Player Error:', error);
            });

            (nextPlayer as any)._playHandler = playHandler;
            (nextPlayer as any)._timeUpdateHandler = timeUpdateHandler;

            isInitializingRef.current = false;
          } catch (error) {
            console.error('Error attaching event handlers:', error);
            isInitializingRef.current = false;
          }
        })
        .catch((error) => {
          isInitializingRef.current = false;
          if (isMounted) {
            console.error('Error initializing Vimeo player:', error);
          }
        });
    } catch (error) {
      console.error('Error creating Vimeo player:', error);
      vimeoPlayer.current = null;
    }

    return () => {
      isMounted = false;
      const finishCleanup = () => {
        destroyCurrentPlayer();
      };

      if (readyPromise) {
        readyPromise.finally(finishCleanup);
      } else {
        finishCleanup();
      }
    };
  }, [videoId, width, height]);

  return { containerRef };
}
